// src/features/members/membersSlice.ts
import {
    createSlice,
    createEntityAdapter,
    createAsyncThunk,
    createSelector,
    PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { Member } from '@/types/member';
import { assetUrl } from '@/utils/assetUrl';
import { getAssetVersion } from '@/utils/versions';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';
type MemberEntity = Member & { id: string };

const membersAdapter = createEntityAdapter<MemberEntity>({
    sortComparer: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
});

type MembersExtraState = {
    status: Status;
    error: string | null;
    loadedAt: number | null;
    version: string | number | null;
};

const initialState = membersAdapter.getInitialState<MembersExtraState>({
    status: 'idle',
    error: null,
    loadedAt: null,
    version: null,
});

function normalizeMembers(data: unknown): Member[] {
    if (Array.isArray(data)) return data as Member[];
    if (data && typeof data === 'object' && Array.isArray((data as any).items)) {
        return (data as any).items as Member[];
    }
    return [];
}

function toEntity(m: Member): MemberEntity {
    const id =
        m.id != null ? String(m.id)
            : m.pathname != null ? String(m.pathname)
                : null;
    if (!id) throw new Error('Member without id or pathname');
    return { ...m, id };
}

export const fetchMembers = createAsyncThunk<
    { items: MemberEntity[]; version: string | number | null },
    { force?: boolean } | void,
    { state: RootState; rejectValue: string }
>(
    'members/fetchAll',
    async (arg, { getState, rejectWithValue }) => {
        const force = typeof arg === 'object' && !!arg?.force;
        const { loadedAt, version: currentVersion } = getState().members;
        const TTL_MS = 60_000;

        try {
            const manifestVersion = await getAssetVersion('members.json');

            if (!force && manifestVersion != null && currentVersion != null &&
                String(manifestVersion) === String(currentVersion)) {
                return rejectWithValue('fresh');
            }
            if (!force && manifestVersion == null && loadedAt && Date.now() - loadedAt < TTL_MS) {
                return rejectWithValue('fresh');
            }

            const baseUrl = assetUrl('members.json');
            const qsV = manifestVersion != null ? `v=${encodeURIComponent(String(manifestVersion))}&` : '';
            const url = `${baseUrl}?${qsV}t=${Date.now()}`;

            if (import.meta.env.DEV) console.debug('[members] GET', url);

            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) {
                const txt = await res.text().catch(() => '');
                if (import.meta.env.DEV) console.error('[members] HTTP', res.status, txt.slice(0, 160));
                return rejectWithValue(`HTTP ${res.status}`);
            }

            let raw: unknown;
            try {
                raw = await res.json();
            } catch (e: any) {
                const txt = await res.text().catch(() => '');
                if (import.meta.env.DEV) console.error('[members] JSON parse error:', e?.message, txt.slice(0, 160));
                return rejectWithValue('Invalid JSON');
            }

            const items = normalizeMembers(raw).map(toEntity);
            return { items, version: manifestVersion };
        } catch (e: any) {
            if (import.meta.env.DEV) console.error('[members] fetch error:', e);
            return rejectWithValue(e?.message ?? 'Failed to load members');
        }
    },
    {
        condition: (arg, { getState }) => {
            const { status } = (getState() as RootState).members;
            return status !== 'loading'; // блок повторного старта
        },
    }
);

export const membersSlice = createSlice({
    name: 'members',
    initialState,
    reducers: {
        upsertManyMembers: membersAdapter.upsertMany,
        upsertMember: membersAdapter.upsertOne,
        removeMember: membersAdapter.removeOne,
        resetMembers: (s) => {
            membersAdapter.removeAll(s);
            s.status = 'idle';
            s.error = null;
            s.loadedAt = null;
            s.version = null;
        },
    },
    extraReducers: (b) => {
        b.addCase(fetchMembers.pending, (s) => {
            s.status = 'loading';
            s.error = null;
        });
        b.addCase(fetchMembers.fulfilled, (s, a: PayloadAction<{ items: MemberEntity[]; version: string | number | null }>) => {
            s.status = 'succeeded';
            membersAdapter.setAll(s, a.payload.items);
            s.loadedAt = Date.now();
            if (a.payload.version != null) s.version = a.payload.version;
        });
        b.addCase(fetchMembers.rejected, (s, a) => {
            if (a.payload === 'fresh') {
                s.status = 'succeeded';
                return;
            }
            s.status = 'failed';
            s.error = (a.payload as string) ?? 'Unknown error';
        });
    },
});

export const { upsertManyMembers, upsertMember, removeMember, resetMembers } = membersSlice.actions;
export const membersReducer = membersSlice.reducer;

const base = (s: RootState) => s.members;

export const {
    selectAll: selectAllMembers,
    selectById: selectMemberById,
    selectIds: selectMemberIds,
} = membersAdapter.getSelectors<RootState>(base);

export const selectMemberByPathname = createSelector(
    [selectAllMembers, (_: RootState, pathname: string) => pathname],
    (list, pathname) => list.find((m) => String(m.pathname) === String(pathname))
);

export const selectMembersStatus   = (s: RootState) => base(s).status;
export const selectMembersError    = (s: RootState) => base(s).error;
export const selectMembersLoadedAt = (s: RootState) => base(s).loadedAt;
export const selectMembersVersion  = (s: RootState) => base(s).version;
